/*
 * TEMPORARY — demo only.
 *
 * A React port of the reference plugin's rendered widget, mirroring
 * preview/preview.html class for class so the reference stylesheet applies
 * unchanged. Intended to be reverted with the rest of demo/ after the demo.
 *
 * The state contract is the reference's: every render adopts what `normalise`
 * vouched for, so the page can never hold a configuration the engine rejects.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_PATH } from '@src/core/router/paths';
import {
  addonAvailableOn,
  addonList,
  calculate,
  formatCents,
  formatNumber,
  getSegment,
  orderedPlans,
} from './calculator';
import { demoConfig } from './config';
import type { BillingKey, DemoLine, DemoRequestedState } from './types';
import './ticlick-pricing.css';

const UID = 'tk1';

/** Reads the reference's own query parameters, including `tk_addons=a:1,b:2`. */
const readParams = (search: string): DemoRequestedState => {
  const params = new URLSearchParams(search);
  const state: DemoRequestedState = {};

  const plan = params.get('tk_plan');
  if (plan) state.plan = plan;

  const billing = params.get('tk_billing');
  if (billing) state.billing = billing;

  const seats = params.get('tk_seats');
  if (seats !== null) state.seats = Number.parseInt(seats, 10);

  const participants = params.get('tk_participants');
  if (participants !== null)
    state.participants = Number.parseInt(participants, 10);

  const addons = params.get('tk_addons');
  if (addons) {
    state.addons = addons.split(',').reduce<Record<string, number>>(
      (accumulator, pair) => {
        const [key, quantity] = pair.split(':');
        if (key) accumulator[key] = Number.parseInt(quantity ?? '1', 10) || 1;
        return accumulator;
      },
      {}
    );
  }

  return state;
};

const writeParams = (
  search: string,
  state: { plan: string; billing: string; seats: number; participants: number; addons: Record<string, number> }
) => {
  const params = new URLSearchParams(search);
  params.set('tk_plan', state.plan);
  params.set('tk_billing', state.billing);

  if (state.seats > 0) params.set('tk_seats', String(state.seats));
  else params.delete('tk_seats');

  if (state.participants > 0)
    params.set('tk_participants', String(state.participants));
  else params.delete('tk_participants');

  const addons = Object.entries(state.addons)
    .filter(([, quantity]) => quantity > 0)
    .map(([key, quantity]) => `${key}:${quantity}`)
    .join(',');
  if (addons) params.set('tk_addons', addons);
  else params.delete('tk_addons');

  const query = params.toString();
  return query ? `?${query}` : '';
};

const Stepper = ({
  id,
  value,
  min,
  max,
  multiple,
  describedBy,
  onChange,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  multiple: number;
  describedBy: string;
  onChange: (next: number) => void;
}) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    onChange(Number.isFinite(parsed) ? parsed : 0);
  };

  return (
    <div
      className='tk-stepper'
      data-multiple={multiple}
    >
      <button
        type='button'
        className='tk-stepper__btn'
        aria-label='Decrease'
        disabled={value <= min}
        onClick={() => onChange(Math.max(value - 1, min))}
      >
        &minus;
      </button>
      <input
        type='number'
        className='tk-stepper__input'
        id={id}
        inputMode='numeric'
        min={min}
        max={max}
        step={1}
        value={draft}
        aria-describedby={describedBy}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit(draft);
        }}
      />
      <button
        type='button'
        className='tk-stepper__btn'
        aria-label='Increase'
        disabled={value >= max}
        onClick={() => onChange(Math.min(value + 1, max))}
      >
        +
      </button>
      {multiple > 1 ? (
        <span className='tk-stepper__hint'>
          x {multiple}{' '}
          <output className='tk-stepper__total'>
            {formatNumber(value * multiple)}
          </output>
        </span>
      ) : null}
    </div>
  );
};

const SummaryLines = ({
  lines,
  symbol,
}: {
  lines: DemoLine[];
  symbol: string;
}) => (
  <ul className='tk-lines'>
    {lines.map((line) => (
      <li
        className='tk-line'
        key={line.key}
      >
        <span className='tk-line__label'>
          {line.label}
          {line.detail ? (
            <span className='tk-line__detail'>{line.detail}</span>
          ) : null}
        </span>
        <span className='tk-line__amount'>
          {formatCents(line.amount_cents, symbol)}
        </span>
      </li>
    ))}
  </ul>
);

export const TicklickPricingWidget = () => {
  const navigate = useNavigate();
  const config = demoConfig;
  const segment = useMemo(() => getSegment(config), [config]);
  const copy = segment.copy;
  const symbol = config.currency_symbol;
  const plans = useMemo(() => orderedPlans(segment), [segment]);
  const addons = useMemo(() => addonList(segment), [segment]);

  const [requested, setRequested] = useState<DemoRequestedState>(() =>
    readParams(window.location.search)
  );
  const [hasInteracted, setHasInteracted] = useState(false);

  const quote = useMemo(
    () => calculate(config, segment.key, requested),
    [config, segment.key, requested]
  );

  // Adopt the normalised state: controls render from the quote, never from
  // what was requested.
  const state = quote.state;
  const plan = segment.plans[state.plan];
  const annual = state.billing === 'annual';

  const change = useCallback((patch: DemoRequestedState) => {
    setHasInteracted(true);
    setRequested((previous) => ({ ...previous, ...patch }));
  }, []);

  const toggleAddon = (key: string, on: boolean) =>
    change({ addons: { ...state.addons, [key]: on ? 1 : 0 } });

  const handleCheckout = () => {
    navigate(
      `${PUBLIC_PATH.REGISTER}${writeParams(window.location.search, state)}`
    );
  };

  /*
   * The URL mirrors the configuration, but only after the first interaction,
   * and the write is guarded: browsers throttle history writes and then throw,
   * which the steppers can reach. Both are defects the reference documents in
   * its own handover; they are fixed here rather than reproduced.
   */
  const writeTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!hasInteracted) return;
    window.clearTimeout(writeTimer.current);
    writeTimer.current = window.setTimeout(() => {
      try {
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${writeParams(window.location.search, state)}${window.location.hash}`
        );
      } catch {
        /* throttled: the page is still correct, only the URL is skipped */
      }
    }, 200);
    return () => window.clearTimeout(writeTimer.current);
  }, [hasInteracted, state]);

  const seatCheckoutMax = plan.seats.hard_stop_total - plan.seats.included;
  const seatsMax = seatCheckoutMax + (segment.seats.flex_overshoot || 5);

  return (
    <section
      className='tk-pricing'
      id={UID}
      data-segment={segment.key}
      data-billing={state.billing}
      data-plan={state.plan}
      aria-labelledby={`${UID}-title`}
    >
      <header className='tk-head'>
        <h2
          className='tk-title'
          id={`${UID}-title`}
        >
          {copy.heading}
        </h2>
        <p className='tk-sub'>{copy.subheading}</p>
      </header>

      <fieldset className='tk-billing'>
        <legend className='tk-billing__legend'>
          {copy.billing_toggle_label}
        </legend>
        <div className='tk-billing__control'>
          {(['annual', 'monthly'] as BillingKey[]).map((key) => (
            <span
              key={key}
              style={{ display: 'contents' }}
            >
              <input
                type='radio'
                className='tk-billing__input'
                name={`${UID}-billing`}
                id={`${UID}-billing-${key}`}
                value={key}
                checked={state.billing === key}
                onChange={() => change({ billing: key })}
              />
              <label
                className='tk-billing__label'
                htmlFor={`${UID}-billing-${key}`}
              >
                {segment.billing[key].label}
                {segment.billing[key].note ? (
                  <span className='tk-billing__note'>
                    {segment.billing[key].note}
                  </span>
                ) : null}
              </label>
            </span>
          ))}
        </div>
      </fieldset>

      <fieldset className='tk-plans'>
        <legend className='tk-sr-only'>Choose a plan</legend>
        <ul className='tk-plans__list'>
          {plans.map((candidate) => {
            const compareTo = candidate.compare_to
              ? segment.plans[candidate.compare_to]
              : undefined;
            return (
              <li
                className='tk-plan'
                data-tk-plan={candidate.key}
                key={candidate.key}
              >
                <input
                  type='radio'
                  className='tk-plan__input'
                  name={`${UID}-plan`}
                  id={`${UID}-plan-${candidate.key}`}
                  value={candidate.key}
                  checked={state.plan === candidate.key}
                  onChange={() => change({ plan: candidate.key })}
                />
                <label
                  className='tk-plan__card'
                  htmlFor={`${UID}-plan-${candidate.key}`}
                >
                  <span className='tk-plan__badge'>{candidate.badge}</span>
                  <span className='tk-plan__name'>{candidate.name}</span>
                  <span className='tk-plan__price'>
                    <span className='tk-price tk-price--annual'>
                      <span className='tk-plan__amount'>
                        {formatCents(
                          candidate.price.annual_per_month_cents,
                          symbol
                        )}
                      </span>
                      <span className='tk-plan__per'>{copy.per_month}</span>
                    </span>
                    <span className='tk-price tk-price--monthly'>
                      <span className='tk-plan__amount'>
                        {formatCents(
                          candidate.price.monthly_per_month_cents,
                          symbol
                        )}
                      </span>
                      <span className='tk-plan__per'>{copy.per_month}</span>
                    </span>
                  </span>
                  <span className='tk-plan__billnote'>
                    <span className='tk-price tk-price--annual'>
                      {formatCents(candidate.price.annual_total_cents, symbol)}{' '}
                      {copy.billed_annually_note}
                    </span>
                    <span className='tk-price tk-price--monthly'>
                      {copy.billed_monthly_note}
                    </span>
                  </span>
                  <span className='tk-plan__compare'>
                    {compareTo
                      ? `Everything in ${compareTo.name}, plus:`
                      : ''}
                  </span>
                  <span className='tk-plan__desc'>{candidate.description}</span>
                  <span className='tk-plan__headline'>
                    {candidate.headline.label}
                  </span>
                  <span className='tk-plan__bullets'>
                    {candidate.bullets.map((bullet) => (
                      <span
                        className='tk-plan__bullet'
                        key={bullet}
                      >
                        {bullet}
                      </span>
                    ))}
                  </span>
                  <span className='tk-plan__cta'>Select plan</span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>

      <div className='tk-config'>
        <div className='tk-config__main'>
          <h3 className='tk-config__title'>{copy.configure_heading}</h3>
          <p className='tk-config__sub'>{copy.configure_subheading}</p>

          {quote.removed.length > 0 || quote.adjusted.length > 0 ? (
            <div
              className='tk-notice tk-notice--warn'
              role='status'
            >
              <p className='tk-notice__title'>{copy.removed_notice_title}</p>
              <ul className='tk-notice__list'>
                {[...quote.removed, ...quote.adjusted].map((report) => (
                  <li key={`${report.key}-${report.note}`}>{report.note}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {quote.flex.length > 0 ? (
            <div
              className='tk-notice tk-notice--flex'
              role='status'
            >
              <p className='tk-notice__title'>{copy.flex_notice_title}</p>
              <ul className='tk-notice__list'>
                {quote.flex.map((entry) => (
                  <li key={entry.code}>{entry.note}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div
            className='tk-item'
            data-tk-item='seats'
          >
            <div className='tk-item__text'>
              <label
                className='tk-item__label'
                htmlFor={`${UID}-seats`}
              >
                {copy.seats_label}
              </label>
              <p
                className='tk-item__help'
                id={`${UID}-seats-help`}
              >
                {copy.seats_help}{' '}
                <span>
                  {plan.seats.included} included with {plan.name}.
                </span>
              </p>
              <p className='tk-item__price'>
                {formatCents(plan.seats.extra_monthly_cents, symbol)}
                {copy.per_month} each
                {annual ? (
                  <span className='tk-price tk-price--annual'>
                    <span className='tk-item__billed'>
                      {copy.billed_annually_note}
                    </span>
                  </span>
                ) : null}
              </p>
            </div>
            <Stepper
              id={`${UID}-seats`}
              value={state.seats}
              min={0}
              max={seatsMax}
              multiple={1}
              describedBy={`${UID}-seats-help`}
              onChange={(seats) => change({ seats })}
            />
          </div>

          <div
            className='tk-item'
            data-tk-item='participants'
          >
            <div className='tk-item__text'>
              <label
                className='tk-item__label'
                htmlFor={`${UID}-participants`}
              >
                {copy.participants_label}
              </label>
              <p
                className='tk-item__help'
                id={`${UID}-participants-help`}
              >
                {copy.participants_help}{' '}
                <span>
                  {formatNumber(plan.participants_included)} included with{' '}
                  {plan.name}.
                </span>
              </p>
              <p className='tk-item__price'>
                {formatCents(segment.participants.unit_cents, symbol)} per
                participant, {copy.billed_annually_note}
              </p>
              <details className='tk-policy'>
                <summary>What if we go over?</summary>
                <p>{segment.participants.overage_policy}</p>
              </details>
            </div>
            <Stepper
              id={`${UID}-participants`}
              value={state.participants}
              min={0}
              max={segment.participants.max_blocks}
              multiple={segment.participants.block_size}
              describedBy={`${UID}-participants-help`}
              onChange={(participants) => change({ participants })}
            />
          </div>

          <div className='tk-addons'>
            <h4 className='tk-addons__title'>Add-ons</h4>
            {addons.map((addon) => {
              const available = addonAvailableOn(addon, state.plan);
              const unitCents = addon.annual_only
                ? addon.annual_cents
                : annual
                  ? addon.annual_cents
                  : addon.monthly_cents;
              return (
                <div
                  className='tk-item tk-item--addon'
                  data-tk-item='addon'
                  data-tk-addon={addon.key}
                  key={addon.key}
                >
                  <div className='tk-item__text'>
                    <div className='tk-switch'>
                      <input
                        type='checkbox'
                        className='tk-switch__input'
                        id={`${UID}-addon-${addon.key}`}
                        aria-describedby={`${UID}-addon-${addon.key}-help`}
                        checked={(state.addons[addon.key] ?? 0) > 0}
                        disabled={!available}
                        onChange={(event) =>
                          toggleAddon(addon.key, event.target.checked)
                        }
                      />
                      <label
                        className='tk-switch__label'
                        htmlFor={`${UID}-addon-${addon.key}`}
                      >
                        <span
                          className='tk-switch__track'
                          aria-hidden='true'
                        >
                          <span className='tk-switch__thumb' />
                        </span>
                        <span className='tk-item__label'>
                          {addon.name}
                          {addon.subtitle ? (
                            <span className='tk-item__subtitle'>
                              {addon.subtitle}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </div>
                    <p
                      className='tk-item__help'
                      id={`${UID}-addon-${addon.key}-help`}
                    >
                      {addon.description}
                      {addon.note ? (
                        <span className='tk-item__note'>{addon.note}</span>
                      ) : null}
                      {!available && addon.unavailable_note ? (
                        <span className='tk-item__gate'>
                          {addon.unavailable_note}
                        </span>
                      ) : null}
                    </p>
                    {addon.explainer ? (
                      <details className='tk-policy tk-policy--addon'>
                        <summary>{addon.explainer_label}</summary>
                        <p>{addon.explainer}</p>
                      </details>
                    ) : null}
                    <p className='tk-item__price'>
                      {addon.price_from ? (
                        <span className='tk-item__from'>From</span>
                      ) : null}
                      {formatCents(addon.monthly_cents, symbol)}
                      {copy.per_month}
                      {addon.price_note ? (
                        <span className='tk-item__pricenote'>
                          {addon.price_note}
                        </span>
                      ) : null}
                      {annual || addon.annual_only ? (
                        <span className='tk-price tk-price--annual'>
                          <span className='tk-item__billed'>
                            {copy.billed_annually_note}
                          </span>
                        </span>
                      ) : null}
                      <span className='tk-sr-only'>
                        {formatCents(unitCents, symbol)}
                      </span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside
          className='tk-summary'
          aria-labelledby={`${UID}-summary-title`}
        >
          <div className='tk-summary__inner'>
            <h3
              className='tk-summary__title'
              id={`${UID}-summary-title`}
            >
              {copy.summary_heading}
            </h3>
            <div
              className='tk-summary__body'
              aria-live='polite'
            >
              {quote.routes_to_flex ? (
                <>
                  <p className='tk-summary__flex'>Custom pricing</p>
                  <p className='tk-summary__flexnote'>
                    This configuration is priced with our team.
                  </p>
                </>
              ) : (
                <>
                  <SummaryLines
                    lines={quote.recurring}
                    symbol={symbol}
                  />

                  {quote.annual_once.length > 0 ? (
                    <div className='tk-lines__group'>
                      {!annual ? (
                        <>
                          <p className='tk-lines__grouptitle'>
                            {copy.annual_extras_heading}
                          </p>
                          <p className='tk-lines__groupnote'>
                            {copy.annual_extras_note}
                          </p>
                        </>
                      ) : null}
                      <SummaryLines
                        lines={quote.annual_once}
                        symbol={symbol}
                      />
                    </div>
                  ) : null}

                  <div className='tk-total'>
                    {!annual ? (
                      <>
                        <div className='tk-total__row'>
                          <span>Per month</span>
                          <span className='tk-total__amount'>
                            {formatCents(quote.recurring_total_cents, symbol)}
                          </span>
                        </div>
                        {quote.annual_once_total_cents > 0 ? (
                          <div className='tk-total__row tk-total__row--muted'>
                            <span>Annual extras</span>
                            <span className='tk-total__amount'>
                              {formatCents(
                                quote.annual_once_total_cents,
                                symbol
                              )}
                            </span>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    <div className='tk-total__row tk-total__row--grand'>
                      <span>{annual ? 'Total per year' : 'Due today'}</span>
                      <span className='tk-total__amount'>
                        {formatCents(quote.due_today_cents, symbol)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='tk-summary__actions'>
              <button
                type='button'
                className='tk-btn tk-btn--primary'
                hidden={!quote.can_checkout}
                onClick={handleCheckout}
              >
                {copy.checkout_cta}
              </button>
              <a
                className='tk-btn tk-btn--sales'
                href='#contact'
                hidden={quote.can_checkout}
              >
                {copy.sales_cta}
              </a>
              <a
                className='tk-btn tk-btn--trial'
                href='#trial'
                hidden={!quote.can_checkout}
                onClick={(event) => {
                  event.preventDefault();
                  handleCheckout();
                }}
              >
                {copy.trial_cta}
              </a>
              <p
                className='tk-summary__trialnote'
                hidden={!quote.can_checkout}
              >
                {copy.trial_note}
              </p>
            </div>
          </div>
        </aside>
      </div>

      <details className='tk-compare'>
        <summary className='tk-compare__toggle'>
          <span>{copy.compare_toggle_open}</span>
        </summary>
        <div className='tk-compare__body'>
          <h3 className='tk-compare__title'>{copy.compare_heading}</h3>
          <div
            className='tk-compare__scroll'
            tabIndex={0}
            role='region'
            aria-label={copy.compare_heading}
          >
            <table className='tk-table'>
              <caption className='tk-sr-only'>{copy.compare_heading}</caption>
              <thead>
                <tr>
                  <th scope='col'>Feature</th>
                  {plans.map((candidate) => (
                    <th
                      scope='col'
                      key={candidate.key}
                    >
                      {candidate.name}
                    </th>
                  ))}
                </tr>
              </thead>
              {segment.comparison.map((group) => (
                <tbody key={group.group}>
                  <tr className='tk-table__grouprow'>
                    <th
                      scope='colgroup'
                      colSpan={plans.length + 1}
                    >
                      {group.group}
                    </th>
                  </tr>
                  {group.rows.map((row) => (
                    <tr
                      key={row.label}
                      className={
                        row.highlight ? 'tk-table__row--highlight' : undefined
                      }
                    >
                      <th scope='row'>
                        {row.label}
                        {row.footnote_ref ? '*' : ''}
                      </th>
                      {plans.map((candidate) => {
                        const value = row.values[candidate.key];
                        const level = segment.module_levels[value];
                        return (
                          <td key={candidate.key}>
                            {level ? (
                              <abbr title={level}>{value}</abbr>
                            ) : (
                              value
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {group.footnote ? (
                    <tr>
                      <td
                        className='tk-table__footnote'
                        colSpan={plans.length + 1}
                      >
                        {group.footnote}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </details>
    </section>
  );
};

export default TicklickPricingWidget;
